import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import * as uuid from 'uuid';

@Injectable({
  providedIn: 'root'
})
/**
 * generic sync utils to provide supportive features
 */
export class UtilsService {

  constructor() { }

  /**
   * log a given error, then throw it
   * @param msg error message to be logged and displayed
   */
  raiseError = (msg: string): Observable<never> => {
    console.error(msg);
    return throwError(() => new Error(msg));
  }

  /**
   * convert a blob (binary large object) into a file
   * @param blob blob to be converted
   * @param fileName name of the new file
   * @returns resulting file
   */
  blobToFile(blob: Blob, fileName: string): File {
    const b: any = blob;
    b.lastModified = Date.now();
    b.name = fileName;
    b.webkitRelativePath = '';
    return blob as File;
  }

  /**
   * @param serial serial ID of the scanner
   * @param isVWS whether the new calculation via VWS should be done
   * @param isPrecise whether this is a precise or a quick measurement
   * @param isHrv whether this is an HRV measurement
   * @param isHrvTest whether this is a HRV test measurement
   * @param language user language
   * @param data measured data
   * @param additionalData any additional data the backend might require
   */
  genMeasurementRequestMetadata = (
    serial: string,
    isVWS = true,
    isPrecise = true,
    isHrv = false,
    isHrvTest = false,
    language = 'de',
    data = '',
    additionalData?: any,
  ) => {
    additionalData = additionalData ? additionalData : {};
    if (serial.startsWith('1E') || serial.startsWith('1N')) {
      additionalData.model = 'MRSRRS_SCN05';
      additionalData.veggie_04 = true;
    } else if (serial.startsWith('2E') || serial.startsWith('2N')) {
      additionalData.model = 'MRSRRS_SCN06';
      additionalData.veggie_04 = true;
    } else {
      additionalData.veggie_03 = true;
    }
    additionalData.uuid = uuid.v4();
    if (isPrecise) {
      additionalData.AOX_extended_final = 'yes';
      if (isVWS) {
        additionalData.measurement_type = 'aox_precise';
      }
    } else {
      if (isVWS) {
        additionalData.measurement_type = 'aox';
      }
      if (isHrv) {
        additionalData.hrv = 'true';
      }
      if (isHrvTest) {
        additionalData.hrv_test = 'true';
      }
    }

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8');
    const body = {
      Serial: serial,
      MeasurementBlocks: data,
      UserID: 'ffffffffffffffffffffffffffffffff',
      lang: language,
      AdditionalData: JSON.stringify(additionalData),
    };

    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
    const file = new File([blob], 'measure.json', { lastModified: Date.now() });
    const form = new FormData();
    form.append('measure_json', file);
    return { form, headers };
  }

  /**
   * some requests require a separate body in addition to the information provided by 'genMeasurementRequestMetadata'
   * @param serial serial ID of the scanner
   * @param language user language
   * @param data measured data
   * @param additionalData any additional data the backend might require
   */
  genMeasurementRequestBody = (
    serial: string,
    language: string,
    data = '',
    additionalData: any
  ): string => {
    let body = `Serial=${serial}`;
    body += `&MeasurementBlocks=${data === '' ? '' : encodeURIComponent(data)}`;
    body += `&UserID=ffffffffffffffffffffffffffffffff`;
    body += `&lang=${language}`;
    if (additionalData) {
      body += `&AdditionalData=${JSON.stringify(additionalData)}`;
    }
    return body;
  }
}
